import { z } from 'zod';

/**
 * 吹き出し式テロップ (background: 'bubble'。TikTok / Instagram リール / YouTube ショートの「コメントに返信」
 * ステッカー風) の値と寸法の式 (docs/features/caption-bubble-template-plan.md)。
 *
 * - 全行を 1 つの角丸の箱で囲む (box は行ごとの箱)。見出し・アイコン・しっぽの付け方は**スタイル**で変わる
 *   (CAPTION_BUBBLE_STYLE_PRESETS。各アプリの実画面の比率を実測した値)
 * - 寸法はすべて**本文のフォントサイズに対する倍率 (em)** で持ち、フィット縮小した後のフォントサイズを
 *   掛けて px にする (縮小すると吹き出しごと縮む)。プレビュー (remotion/EditVideo)・MP4 書き出し
 *   (video-editor の同名ファイル)・合成フレーム (backend caption_bubble.py / contact_sheet) が同じ式を使う
 *
 * 'bubble' は captionCatalog の CAPTION_BACKGROUNDS には入れない (AI の選択肢に出さない。利用者が
 * テンプレート / 編集 UI で選んだときだけ使う)。video-editor リポジトリの src/schema/captionBubble.ts にも同じ定義がある。
 */

export const CAPTION_BUBBLE_BACKGROUND = 'bubble' as const;

export const CAPTION_BUBBLE_STYLES = ['tiktok', 'instagram', 'youtube'] as const;
export type CaptionBubbleStyle = (typeof CAPTION_BUBBLE_STYLES)[number];

export const CAPTION_BUBBLE_TAILS = ['bottomLeft', 'bottomRight', 'topLeft', 'topRight', 'none'] as const;
export type CaptionBubbleTail = (typeof CAPTION_BUBBLE_TAILS)[number];

/** 見出しの最大文字数 (コードポイント)。 */
export const CAPTION_BUBBLE_HEADER_MAX_CHARS = 40;
export const CAPTION_BUBBLE_HEADER_SCALE_MIN = 0.4;
export const CAPTION_BUBBLE_HEADER_SCALE_MAX = 0.9;

export interface CaptionBubbleStylePreset {
  /**
   * 箱の組み方。comment = [アイコン][見出し / 本文] の左揃え (コメント返信)。
   * questionBox = 上に色の帯 (見出しを中央に) + 下に本文を中央揃え (Instagram ストーリーズの質問スタンプ)。
   * questionBox ではアイコンを描かない。
   */
  layout: 'comment' | 'questionBox';
  /** 見出しを本文の上に置くか下に置くか (comment のみ。questionBox は常に上の帯)。 */
  headerPlacement: 'above' | 'below';
  /** アイコンを文字の列の上下中央に置くか、上端に揃えるか。 */
  avatarAlign: 'center' | 'top';
  /** アイコンの直径。 */
  avatarEm: number;
  /** 箱の内側の余白 (上・右・下・左)。 */
  padTopEm: number;
  padRightEm: number;
  padBottomEm: number;
  padLeftEm: number;
  /** アイコンと文字の列の間隔。 */
  avatarGapEm: number;
  /** 見出しと本文の間隔。 */
  headerGapEm: number;
  /** 角丸の半径 (design.backgroundRadiusPx 未指定時)。 */
  radiusEm: number;
  /** 見出しの文字サイズ (本文に対する倍率)・行送り・ウェイト・色の既定。 */
  headerScale: number;
  headerLineHeight: number;
  headerWeight: number;
  headerColor: string;
  /** しっぽの既定 (bubble.tail 未指定時)。 */
  tail: CaptionBubbleTail;
  /** しっぽ (箱の角から真下 / 真上へ伸びる直角三角形) の幅と高さ。 */
  tailWidthEm: number;
  tailHeightEm: number;
  /** 箱の最小幅 (0 = 文字に合わせる)。質問スタンプは文字が短くても一定の幅を持つ。 */
  minWidthEm: number;
  /** questionBox の帯の色と、帯の上下の余白 (見出しの行の外側)。 */
  headerBandColor: string;
  headerBandPadYEm: number;
}

/**
 * スタイルごとの寸法 (em = 本文のフォントサイズに対する倍率)。2026-10-10 に各アプリの画面を実測:
 * - tiktok: 利用者提供のスクリーンショット (「〇〇のコメントに返信」)。見出しは上・灰色の中太・アイコンは上下中央・
 *   右の余白が広い・しっぽは左下の角から真下へ伸びる直角三角形 (しっぽ側の角は丸めない)
 * - instagram: ストーリーズの質問スタンプ (「質問はありますか？」・利用者提供のスクリーンショット 4 枚)。上に黒い帯と
 *   白い太字の見出しを中央に、下に本文を中央揃え・箱は画面幅の約 78% (文字が短くても縮めない)・アイコン / しっぽなし
 * - youtube: YouTube 公式の発表画像 (2022・「Comment」)。見出しは上・アイコンなし・しっぽなし
 */
export const CAPTION_BUBBLE_STYLE_PRESETS: Record<CaptionBubbleStyle, CaptionBubbleStylePreset> = {
  tiktok: {
    layout: 'comment',
    headerPlacement: 'above',
    avatarAlign: 'center',
    avatarEm: 1.66,
    padTopEm: 0.86,
    padRightEm: 1.52,
    padBottomEm: 1.1,
    padLeftEm: 0.38,
    avatarGapEm: 0.45,
    headerGapEm: 0.2,
    radiusEm: 0.34,
    headerScale: 0.49,
    headerLineHeight: 1.3,
    headerWeight: 600,
    headerColor: '#8C8C8C',
    tail: 'bottomLeft',
    tailWidthEm: 1.2,
    tailHeightEm: 0.69,
    minWidthEm: 0,
    headerBandColor: '#111111',
    headerBandPadYEm: 0,
  },
  instagram: {
    layout: 'questionBox',
    headerPlacement: 'above',
    avatarAlign: 'top',
    avatarEm: 0,
    padTopEm: 1.12,
    padRightEm: 1.0,
    padBottomEm: 1.12,
    padLeftEm: 1.0,
    avatarGapEm: 0,
    headerGapEm: 0,
    radiusEm: 0.6,
    headerScale: 0.76,
    headerLineHeight: 1.3,
    headerWeight: 700,
    headerColor: '#FFFFFF',
    tail: 'none',
    tailWidthEm: 1.33,
    tailHeightEm: 0.73,
    minWidthEm: 14.7,
    headerBandColor: '#111111',
    headerBandPadYEm: 0.79,
  },
  youtube: {
    layout: 'comment',
    headerPlacement: 'above',
    avatarAlign: 'top',
    avatarEm: 1.6,
    padTopEm: 1.05,
    padRightEm: 1.5,
    padBottomEm: 1.2,
    padLeftEm: 0.95,
    avatarGapEm: 0.5,
    headerGapEm: 0.26,
    radiusEm: 0.65,
    headerScale: 0.77,
    headerLineHeight: 1.3,
    headerWeight: 400,
    headerColor: '#606060',
    tail: 'none',
    tailWidthEm: 1.33,
    tailHeightEm: 0.73,
    minWidthEm: 0,
    headerBandColor: '#111111',
    headerBandPadYEm: 0,
  },
};

export const CAPTION_BUBBLE_DEFAULTS = {
  style: 'tiktok' as CaptionBubbleStyle,
  /** 吹き出しの地の色 (design.backgroundColor 未指定時)。 */
  backgroundColor: '#FFFFFF',
} as const;

/** 見出しのウェイト: スタイルの既定。本文より太くはしない。backend caption_bubble.py も同じ式。 */
export function captionBubbleHeaderWeight(bodyWeight: number, style: CaptionBubbleStyle = 'tiktok'): number {
  return Math.min(bodyWeight, CAPTION_BUBBLE_STYLE_PRESETS[style].headerWeight);
}

export const CaptionBubbleSchema = z.object({
  // 未指定 = tiktok (第1弾の保存データとの互換)
  style: z.enum(CAPTION_BUBBLE_STYLES).optional(),
  tail: z.enum(CAPTION_BUBBLE_TAILS).optional(),
  // 空・未指定 = 見出し行なし
  headerText: z.string().max(CAPTION_BUBBLE_HEADER_MAX_CHARS * 2).optional(),
  headerColor: z.string().optional(),
  headerScale: z.number().min(CAPTION_BUBBLE_HEADER_SCALE_MIN).max(CAPTION_BUBBLE_HEADER_SCALE_MAX).optional(),
  // 未指定 = アイコンなし。materialId 未指定 = 同梱の既定アイコン
  avatar: z
    .object({
      materialId: z.string().optional(),
      isTemporary: z.boolean().optional(),
      // 署名付き URL の導出値 (stripResolvedUrls が再帰で落とす。保存しない)
      src: z.string().optional(),
    })
    .optional(),
});
export type CaptionBubble = z.infer<typeof CaptionBubbleSchema>;

export interface ResolvedCaptionBubble {
  style: CaptionBubbleStyle;
  preset: CaptionBubbleStylePreset;
  tail: CaptionBubbleTail;
  /** 見出し (空文字 = 見出し行なし)。 */
  headerText: string;
  headerColor: string;
  headerScale: number;
  hasAvatar: boolean;
  avatarMaterialId: string | null;
  avatarSrc: string | null;
}

/** design.bubble を既定値 (スタイルの既定) で補う。 */
export function resolveCaptionBubble(bubble: CaptionBubble | null | undefined): ResolvedCaptionBubble {
  const style: CaptionBubbleStyle =
    bubble?.style && (CAPTION_BUBBLE_STYLES as readonly string[]).includes(bubble.style)
      ? bubble.style
      : CAPTION_BUBBLE_DEFAULTS.style;
  const preset = CAPTION_BUBBLE_STYLE_PRESETS[style];
  const scale = bubble?.headerScale;
  const header = Array.from((bubble?.headerText ?? '').replace(/\s+$/u, '')).slice(0, CAPTION_BUBBLE_HEADER_MAX_CHARS).join('');
  return {
    style,
    preset,
    tail: bubble?.tail && (CAPTION_BUBBLE_TAILS as readonly string[]).includes(bubble.tail) ? bubble.tail : preset.tail,
    headerText: header.trim() ? header : '',
    headerColor: bubble?.headerColor || preset.headerColor,
    headerScale:
      typeof scale === 'number' && Number.isFinite(scale)
        ? Math.min(CAPTION_BUBBLE_HEADER_SCALE_MAX, Math.max(CAPTION_BUBBLE_HEADER_SCALE_MIN, scale))
        : preset.headerScale,
    hasAvatar: !!bubble?.avatar,
    avatarMaterialId: bubble?.avatar?.materialId || null,
    avatarSrc: bubble?.avatar?.src || null,
  };
}

export interface CaptionBubbleLayout {
  /** 見出し行の高さ (em。見出しなしは 0)。 */
  headerHeightEm: number;
  /** 見出しと本文の間隔 (em。見出しなしは 0)。 */
  headerGapEm: number;
  /** 本文の高さ (em) = 行送り × 行数。 */
  bodyHeightEm: number;
  /** アイコンの直径 (em。アイコンなしは 0)。 */
  avatarEm: number;
  /** 文字の列 (見出し + 間隔 + 本文) の高さ (em)。 */
  textHeightEm: number;
  /** 箱の中身 (アイコン・文字の列) の高さ (em。余白を含まない)。 */
  contentHeightEm: number;
  /** 本文の幅に足す横の大きさ (em) = 左右の余白 + アイコン + 間隔。フィット縮小の extraEm。 */
  extraWidthEm: number;
  /** 箱の高さ (em) = 上下の余白 + 中身。しっぽを含まない。 */
  boxHeightEm: number;
  /** しっぽが上下に足す高さ (em。なしは 0)。 */
  tailHeightEm: number;
}

/** 吹き出しの寸法 (em)。本文の行送り (lineHeight。行間の倍率) と行数・スタイルから決まる。 */
export function captionBubbleLayout(bubble: ResolvedCaptionBubble, lineHeight: number, lineCount: number): CaptionBubbleLayout {
  const p = bubble.preset;
  // questionBox の見出しの高さは帯の高さ (上下の余白込み)。帯は箱の上端に付き、余白 (padTop) は帯の下から
  const headerHeightEm = bubble.headerText
    ? bubble.headerScale * p.headerLineHeight + (p.layout === 'questionBox' ? p.headerBandPadYEm * 2 : 0)
    : 0;
  const headerGapEm = bubble.headerText ? p.headerGapEm : 0;
  const bodyHeightEm = lineHeight * Math.max(1, lineCount);
  const avatarEm = bubble.hasAvatar && p.layout === 'comment' ? p.avatarEm : 0;
  const textHeightEm = headerHeightEm + headerGapEm + bodyHeightEm;
  const contentHeightEm = Math.max(avatarEm, textHeightEm);
  return {
    headerHeightEm,
    headerGapEm,
    bodyHeightEm,
    avatarEm,
    textHeightEm,
    contentHeightEm,
    extraWidthEm: p.padLeftEm + p.padRightEm + (avatarEm > 0 ? avatarEm + p.avatarGapEm : 0),
    boxHeightEm: p.padTopEm + contentHeightEm + p.padBottomEm,
    // 質問スタンプ (questionBox) はしっぽを描かない
    tailHeightEm: bubble.tail === 'none' || p.layout === 'questionBox' ? 0 : p.tailHeightEm,
  };
}

/**
 * 吹き出しのブロックの高さが、同じ行数の通常のテロップ (行送り × 行数) より高いぶん (em)。
 * 縦位置のアンカー・はみ出しクランプのブロック高さに足す。
 */
export function captionBubbleExtraHeightEm(layout: CaptionBubbleLayout): number {
  return layout.boxHeightEm + layout.tailHeightEm - layout.bodyHeightEm;
}

/**
 * 本文の 1 行目の上端の、箱の上端からの位置 (em)。アイコンの上揃え・見出しの位置を決めるのに使う。
 * 見出しが上なら「余白 + 見出し + 間隔」、下なら「余白」。文字の列がアイコンより低く上下中央に置くスタイルでは
 * 列ごと下がる (avatarAlign = center)。
 */
export function captionBubbleTextTopEm(bubble: ResolvedCaptionBubble, layout: CaptionBubbleLayout): number {
  const p = bubble.preset;
  const columnOffset = p.avatarAlign === 'center' ? (layout.contentHeightEm - layout.textHeightEm) / 2 : 0;
  return p.padTopEm + columnOffset;
}

export interface CaptionBubbleTailGeometry {
  /** しっぽを付ける箱の角 (左右・上下)。外接矩形はその角の真下 (真上) に、箱の辺と揃えて置く。 */
  horizontal: 'left' | 'right';
  vertical: 'top' | 'bottom';
  /** 外接矩形の大きさ (px)。高さは箱へ 1px 食い込ませるぶんを含む (境目に線が出ないように)。 */
  widthPx: number;
  heightPx: number;
  /** 外接矩形の左上を原点とする三角形の 3 点 (px)。 */
  points: Array<[number, number]>;
}

/**
 * しっぽの形: 箱の角から真下 (上のしっぽは真上) へ伸びる直角三角形。外側の辺は箱の左 (右) の辺の延長で、
 * 先端は角の真下。箱の幅に依らず角からの位置だけで置ける (描画時に箱の幅が分からないため)。
 * しっぽを付けた角は丸めない (BubbleFrame / contact_sheet)。
 */
export function captionBubbleTail(
  tail: CaptionBubbleTail,
  fontSizePx: number,
  preset: CaptionBubbleStylePreset,
): CaptionBubbleTailGeometry | null {
  if (tail === 'none') return null;
  const w = preset.tailWidthEm * fontSizePx;
  const h = preset.tailHeightEm * fontSizePx + 1;
  const left = tail === 'bottomLeft' || tail === 'topLeft';
  const bottom = tail === 'bottomLeft' || tail === 'bottomRight';
  const baseY = bottom ? 0 : h;
  const tipY = bottom ? h : 0;
  const points: Array<[number, number]> = left
    ? [[0, baseY], [w, baseY], [0, tipY]]
    : [[0, baseY], [w, baseY], [w, tipY]];
  return { horizontal: left ? 'left' : 'right', vertical: bottom ? 'bottom' : 'top', widthPx: w, heightPx: h, points };
}
